import React from "react";
import PropTypes from "prop-types";
import { pure } from "recompose";
import styled from "styled-components";
import { ResultHeader } from "@witbooking/react-components";
import constants from "../../utils/constants";
import media from "../../utils/media";
import ResultBlock from "../ResultBlock/ResultBlock";
import { greyBasic, greyLighter } from "../../theme/variables";
import PropertyList from "../PropertyList/PropertyList";

const StyledResultHeader = styled(ResultHeader)`
  border-bottom: ${props => (props.withGrid ? `1px solid #e1e1e1` : `none`)};
  margin-bottom: 0;

  ${media.up.md`
    border-bottom: 0;
  `};

  ${media.up.md`
    padding: 0;
    margin-bottom: 20px;
  `};
`;

const MobileTriangle = styled.div`
  width: 10px;
  height: 10px;
  background-color: ${greyBasic};
  border-bottom: 1px solid ${greyLighter};
  border-right: 1px solid ${greyLighter};
  transform: rotate(45deg);
  position: absolute;
  right: 10%;
  bottom: 5px;

  ${media.up.md`
  display: none;
  `};
`;
const MobileTriangleWrapper = styled.div`
  position: relative;
  height: 10px;
  ${media.up.md`
    display: none;
  `};
`;
const ResultSummaryDiv = styled.div`
  display: block;
  ${media.up.md`
    display: none;
  `};
`;

function Property({
  entity,
  nature,
  isMainPropertyChain,
  onImageClick,
  onBack,
  onHeaderClick,
  filters,
  resultsSummary,
  buttonBar,
  rooms,
  basePropertyTicker,
  withGrid,
}) {
  const propertyAddress = entity.contact && entity.contact.address;
  const propertyCity = entity.contact && entity.contact.city;
  const propertyPicture = entity.media && entity.media[0] && entity.media[0].url;

  const header = nature !== constants.RESULT_HEADER_NATURE.LARGE && (
    <div>
      <StyledResultHeader
        nature={nature}
        hasBackButton={isMainPropertyChain}
        title={entity.name}
        subtitle={`${propertyAddress}, ${propertyCity}`}
        image={propertyPicture}
        onClick={onHeaderClick}
        onImageClick={event =>
          onImageClick(entity.media, entity.name, entity.description, null, null, null, event)
        }
        onBack={event => onBack({ event, propertyTicker: basePropertyTicker })}
        withGrid={withGrid}
      />
      {!isMainPropertyChain && (
        <>
          <MobileTriangleWrapper>
            <MobileTriangle />
          </MobileTriangleWrapper>
          <div id="THN-mobile-container"></div>
          {resultsSummary && <ResultSummaryDiv>{resultsSummary}</ResultSummaryDiv>}
        </>
      )}
    </div>
  );

  return (
    <ResultBlock
      header={header}
      content={rooms}
      nature={nature}
      filters={filters}
      resultsSummary={resultsSummary}
      buttonBar={buttonBar}
    />
  );
}

Property.propTypes = {
  /** Prop which holds the property information */
  entity: PropTypes.object.isRequired,
  /** The component can have different appearances depending on this prop  */
  nature: PropTypes.oneOf([
    constants.RESULT_HEADER_NATURE.SMALL,
    constants.RESULT_HEADER_NATURE.MEDIUM,
    constants.RESULT_HEADER_NATURE.LARGE,
  ]),
  /** When the property being rendered by this component is part of a chain the back button must be shown  */
  isMainPropertyChain: PropTypes.bool.isRequired,
  /** Function to be executed when clicking the back button */
  onBack: PropTypes.func.isRequired,
  /** Function to be executed when clicking in the result's header */
  onHeaderClick: PropTypes.func,
  /** Function to be executed when clicking in property image */
  onImageClick: PropTypes.func.isRequired,
  /** Filter node / content */
  filters: PropTypes.node,
  /** Button bar node / content */
  buttonBar: PropTypes.node,
  /** Node that lists rooms */
  rooms: PropTypes.node.isRequired,
};

export default pure(Property);
